import { useEffect, useState } from "react";
import styles from "./list.module.css";
import axios from "axios";
import { DirectionsRenderer, GoogleMap, Marker, useLoadScript } from "@react-google-maps/api";
import { authData } from "../../../components/GetAuth";

const colors = ["#F9B12C", "#F58030", "#DB1F26", "#E31B4D", "#39A0D9", "#383E98", "#7CC245", "#4B362A", "#86459A", "#D7D95E", "#89A63D", "#FED700"];

const getHomeIconUrl = () => {
    const svgString = `
    <svg id="Layer_1" data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 47.17 53.46"><defs><style>.cls-1{fill:#fff;stroke:#4b362a;stroke-miterlimit:10;stroke-width:3px;}.cls-2{fill:#4b362a;}</style></defs><path class="cls-1" d="M39.47,4.77H10.53a7.62,7.62,0,0,0-7.62,7.62V36a7.62,7.62,0,0,0,7.62,7.62h5.79L22,53.5a3.45,3.45,0,0,0,6,0l5.69-9.85h5.79A7.62,7.62,0,0,0,47.09,36V12.39A7.62,7.62,0,0,0,39.47,4.77Z" transform="translate(-1.41 -3.27)"/><path class="cls-2" d="M22.77,34.27H19.21a2.09,2.09,0,0,1-1.75-1.7,1.83,1.83,0,0,1,0-.33V26.77c0-.27,0-.28-.27-.27a1.79,1.79,0,0,1-1.77-1.77,1.93,1.93,0,0,1,.59-1.4c2.57-2.56,5.14-5.11,7.68-7.7a2,2,0,0,1,2.69,0c2.53,2.57,5.09,5.11,7.64,7.66a1.76,1.76,0,0,1,.6,1.14V25a1.76,1.76,0,0,1-1.46,1.5c-.11,0-.22,0-.33,0s-.21,0-.21.22a.86.86,0,0,0,0,.16v5.32a2.12,2.12,0,0,1-1.33,2,2.81,2.81,0,0,1-.49.09H27.26c-.35-.11-.47-.29-.47-.7V29.29a1,1,0,0,0-.37-.8,1.13,1.13,0,0,0-.64-.18H24.25a1.09,1.09,0,0,0-.61.16.9.9,0,0,0-.4.8v4.37A.59.59,0,0,1,22.77,34.27Z" transform="translate(-1.41 -3.27)"/></svg>
    `;

    return "data:image/svg+xml;base64," + btoa(svgString);
}

const getIconUrl = (color, index) => {
    const svgString = `
    <svg id="Layer_1" data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 38 42.98"><defs><style>.cls-1{fill:#fff;stroke: ${color};stroke-miterlimit:10;stroke-width:3px;}.cls-2{font-size:18px;fill: ${color};font-family:Poppins-SemiBold, Poppins;font-weight:600;}</style></defs><path class="cls-1" d="M36.46,10H13.54a6,6,0,0,0-6,6V34.78a6,6,0,0,0,6,6h4.58l4.51,7.8a2.73,2.73,0,0,0,4.74,0l4.51-7.8h4.58a6,6,0,0,0,6-6V16.05A6,6,0,0,0,36.46,10Z" transform="translate(-6 -8.51)"/><text class="cls-2" transform="translate(16.04 24.01)">${index}</text></svg>
    `;

    return "data:image/svg+xml;base64," + btoa(svgString);
}

const lightenColor = (hex, percent) => {
    const num = parseInt(hex?.replace("#", ""), 16);
    const amt = Math.round(2.55 * percent);
    const R = (num >> 16) + amt;
    const G = ((num >> 8) & 0x00ff) + amt;
    const B = (num & 0x0000ff) + amt;

    return ("#" + (0x1000000 + (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 + (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 + (B < 255 ? (B < 1 ? 0 : B) : 255)).toString(16).slice(1));
}

// Responsive map height: shorter on phones so the map + list still fit
// comfortably above the fold without excessive scrolling.
const useMapHeight = () => {
    const [height, setHeight] = useState(typeof window !== "undefined" && window.innerWidth <= 480 ? 320 : (typeof window !== "undefined" && window.innerWidth <= 900 ? 420 : 600));

    useEffect(() => {
        const onResize = () => {
            if (window.innerWidth <= 480) setHeight(320);
            else if (window.innerWidth <= 900) setHeight(420);
            else setHeight(600);
        };

        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    return height;
}

const DriverCard = ({ allData, driver }) => {
    const { isLoaded } = useLoadScript({
        id: "google-map-script",
        googleMapsApiKey: process.env.REACT_APP_MAP_API_KEY,
        libraries: ["places"],
    });

    const auth = authData();
    const API_TOKEN = auth?.api_token;
    const center = { lat: 51.509865, lng: -0.118092 };
    const [bookingList, setBookingList] = useState([]);
    const [bookingListNew, setBookingListNew] = useState([]);
    const [directions, setDirections] = useState([]);
    const MAX_WAYPOINTS = 20;
    const mapHeight = useMapHeight();

    const containerStyle = {
        width: "100%",
        height: `${mapHeight}px`
    };

    useEffect(() => {
        let bookingListTemp = allData?.map(i => {
            if (i?.driver?.id === driver?.id) {
                return { address: i?.pickup_details?.address, booking_id: i?.booking_id, customer_name: i?.customer_name, id: i?.id, lat: i?.pickup_details?.latitude, lng: i?.pickup_details?.longitude };
            }

            return null;
        });

        bookingListTemp = bookingListTemp?.filter(i => i !== null);
        setBookingList(bookingListTemp);
    }, [allData, driver])

    useEffect(() => {
        if (isLoaded && bookingList?.length > 0) {
            axios.post(process.env.REACT_APP_API_URL + "v1/optimize-route-single", { driver: driver, pickups: bookingList }, {
                headers: {
                    Authorization: `Bearer ${API_TOKEN}`,
                }
            }).then((response) => {

                if (isLoaded && response.data.data) {
                    const service = new window.google.maps.DirectionsService();

                    let currentRoute = response.data.data;

                    const bookings = currentRoute.bookings;
                    if (!bookings || bookings.length < 1) return;

                    setBookingListNew(bookings);

                    const driverLoc = {
                        lat: parseFloat(currentRoute.driver.lat),
                        lng: parseFloat(currentRoute.driver.lng),
                    };

                    // Split into multiple chunks if necessary
                    const chunks = [];
                    for (let i = 0; i < bookings.length; i += MAX_WAYPOINTS) {
                        chunks.push(bookings.slice(i, i + MAX_WAYPOINTS + 1));
                    }

                    chunks.forEach((chunk, chunkIdx) => {
                        const origin = chunkIdx === 0 ? driverLoc : { lat: parseFloat(chunk[0].lat), lng: parseFloat(chunk[0].lng) };
                        const destination = { lat: parseFloat(chunk[chunk.length - 1].lat), lng: parseFloat(chunk[chunk.length - 1].lng) };

                        const waypoints = chunk.slice(0, -1).map((stop) => ({
                            location: new window.google.maps.LatLng(
                                parseFloat(stop.lat),
                                parseFloat(stop.lng)
                            ),
                            stopover: true,
                        }));

                        const options = { origin, destination, waypoints, travelMode: window.google.maps.TravelMode.DRIVING };

                        service.route(options, (result, status) => {
                            if (status === "OK" && result) {
                                setDirections((prev) => [...prev, { idx: chunkIdx, result, driver: currentRoute.driver.id }]);
                            }
                        });
                    });
                }
            }).catch((error) => { });
        }
    }, [isLoaded, driver, bookingList, API_TOKEN]);

    return (<div className={styles.ThirdStepMapListSec}>
        <div className={styles.ListSec}>
            <div className={styles.Container}>
                <div className={styles.HeadSec}>
                    <p className={styles.DriverName}>{driver?.name}</p>
                    <p className={styles.DriverAddress}>{driver?.address}</p>
                </div>
                <div className={styles.PickupListSec}>
                    {bookingList?.length > 0 && (<p className={styles.PickupTitle}>Pickups: {bookingList?.length}</p>)}
                    <div className={styles.PickupListScroll}>
                        {bookingListNew?.map((i, index) => {
                            return (<div className={styles.PickupList} key={i?.id ?? index}>
                                <div className={styles.PickupTextGroup}>
                                    <span className={styles.PickupIndex}>{index + 1}</span>
                                    <div>
                                        <p><strong>{i?.booking_id}</strong></p>
                                        <p className={styles.PickupAddress}>{i?.address}</p>
                                    </div>
                                </div>
                            </div>);
                        })}

                        {bookingList?.length === 0 && (
                            <div className={styles.EmptyState}>No pickups assigned to this driver</div>
                        )}
                    </div>
                </div>
            </div>
        </div>
        <div className={styles.MapSec}>
            {isLoaded && (<GoogleMap
                mapContainerStyle={containerStyle}
                center={center}
                zoom={13}
            >
                {directions.map((d, i) => {
                    return (<div key={`${d.driver}-${d.idx}-${i}`}>
                        <DirectionsRenderer
                            directions={d.result}
                            options={{
                                suppressMarkers: true,
                                polylineOptions: {
                                    strokeColor: colors[d?.driver % colors.length],
                                    strokeOpacity: 0.9,
                                    strokeWeight: 8,
                                    zIndex: 1
                                }
                            }}
                        />

                        <DirectionsRenderer
                            directions={d.result}
                            options={{
                                suppressMarkers: true,
                                polylineOptions: {
                                    strokeColor: lightenColor(colors[d?.driver % colors.length], 50),
                                    strokeOpacity: 1,
                                    strokeWeight: 4,
                                    zIndex: 2
                                }
                            }}
                        />

                        {d.result.routes[0].legs.map((leg, legIndex) => {
                            return (<div key={legIndex}>
                                {legIndex === 0 && d?.idx === 0 && (<Marker
                                    position={leg.start_location}
                                    icon={{
                                        url: getHomeIconUrl(),
                                        scaledSize: new window.google.maps.Size(35, 40),
                                    }}
                                    zIndex={600}
                                />)}

                                <Marker
                                    position={leg.end_location}
                                    icon={{
                                        url: getIconUrl(colors[d?.driver % colors.length], MAX_WAYPOINTS * d?.idx + legIndex + 1),
                                        scaledSize: new window.google.maps.Size(35, 40),
                                    }}
                                    zIndex={500 - legIndex}
                                />
                            </div>);
                        })}
                    </div>);
                })}
            </GoogleMap>)}
        </div>
    </div>);
};

export default DriverCard;